"use client";

import { useEffect, useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatClock,
  NOISE_KINDS,
  type NoiseKind,
  TIMER_CHOICES,
} from "@/lib/tool-kit/study-more/noise";
import { NoisePlayer } from "@/lib/tool-kit/study-more/noise-player";

const RANGE =
  "w-full cursor-pointer accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";

/** English strings. A translated content file overrides these through `ui`. */
export const WHITE_NOISE_GENERATOR_STRINGS = {
  heading: "Noise player",
  kindLabel: "Colour of noise",
  kinds: { white: "White", pink: "Pink", brown: "Brown" },
  kindNotes: {
    white:
      "Equal energy at every pitch, so it sounds like bright, even hiss — a fan or untuned radio.",
    pink: "Energy falls as pitch rises, so it is softer than white — close to steady rain or wind.",
    brown:
      "Falls faster still, so it is deep and rumbling — a distant waterfall or a jet engine heard from inside.",
  },
  play: "Play",
  stop: "Stop",
  volumeLabel: "Volume",
  softnessLabel: "Softness (cuts the highest pitches)",
  softnessHint: "Turn it up to take the edge off white and pink noise.",
  percent: "{value}%",
  timerLabel: "Stop after",
  timerNone: "No timer",
  timerMinutes: "one:{count} minute|other:{count} minutes",
  timerHint:
    "The sound fades out over the last few seconds, so it never cuts off abruptly.",
  stopsIn: "Stops in {time}",
  statusPlaying: "Playing {kind} noise",
  statusStopped: "Stopped",
  statusEnded: "The timer ended and the sound faded out.",
  startHint: "Nothing plays until you press Play. Start with the volume low.",
  safety:
    "Keep the volume low enough to hold a conversation over it. Loud sound played for hours can damage hearing, and this is a sound tool, not a medical device.",
  unsupported:
    "This browser does not support the Web Audio API, so the noise cannot play here.",
  failed:
    "The audio could not start. Check that your device is not muted, then try again.",
  privacy:
    "The noise is generated in your browser as you listen. Nothing is downloaded or uploaded.",
} as const;

export default function WhiteNoiseGeneratorWidget() {
  const strings = useToolUi(WHITE_NOISE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const playerRef = useRef<NoisePlayer | null>(null);
  const deadline = useRef<number | null>(null);
  const [kind, setKind] = useState<NoiseKind>("pink");
  const [volume, setVolume] = useState(25);
  const [softness, setSoftness] = useState(0);
  const [minutes, setMinutes] = useState<string>("0");
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [error, setError] = useState<"unsupported" | "failed" | null>(null);

  useEffect(
    () => () => {
      playerRef.current?.dispose();
      playerRef.current = null;
    },
    []
  );

  const timing = remaining !== null;
  useEffect(() => {
    if (!(playing && timing)) {
      return;
    }
    const id = setInterval(() => {
      if (deadline.current === null) {
        return;
      }
      setRemaining(Math.max(0, (deadline.current - Date.now()) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, [playing, timing]);

  function applyTimer(value: string) {
    const length = Number(value);
    if (length > 0) {
      deadline.current = Date.now() + length * 60_000;
      setRemaining(length * 60);
      playerRef.current?.setTimer(length * 60);
    } else {
      deadline.current = null;
      setRemaining(null);
      playerRef.current?.setTimer(null);
    }
  }

  async function play() {
    if (
      typeof window.AudioContext === "undefined" &&
      !("webkitAudioContext" in window)
    ) {
      setError("unsupported");
      return;
    }
    setError(null);
    setEnded(false);
    playerRef.current ??= new NoisePlayer({
      onEnded: () => {
        setPlaying(false);
        setEnded(true);
        setRemaining(null);
        deadline.current = null;
      },
    });
    try {
      await playerRef.current.start(kind, volume / 100, softness);
    } catch {
      setError("failed");
      return;
    }
    setPlaying(true);
    applyTimer(minutes);
  }

  function stop() {
    playerRef.current?.stop();
    setPlaying(false);
    setRemaining(null);
    deadline.current = null;
  }

  function chooseKind(next: NoiseKind) {
    setKind(next);
    playerRef.current?.setKind(next);
  }

  function chooseVolume(value: number) {
    setVolume(value);
    playerRef.current?.setVolume(value / 100);
  }

  function chooseSoftness(value: number) {
    setSoftness(value);
    playerRef.current?.setSoftness(value);
  }

  function chooseMinutes(value: string) {
    setMinutes(value);
    if (playing) {
      applyTimer(value);
    }
  }

  let status: string = strings.statusStopped;
  if (playing) {
    status = t(strings.statusPlaying, {
      kind: strings.kinds[kind].toLowerCase(),
    });
  } else if (ended) {
    status = strings.statusEnded;
  }

  return (
    <div className={`${CARD} space-y-6`}>
      <h2 className={WIDGET_H2}>{strings.heading}</h2>
      <PillGroup
        label={strings.kindLabel}
        name="wn-kind"
        onChange={chooseKind}
        options={NOISE_KINDS.map((value) => ({
          value,
          label: strings.kinds[value],
        }))}
        value={kind}
      />
      <p className={HINT}>{strings.kindNotes[kind]}</p>

      <div className="flex flex-wrap items-center gap-4">
        <button
          aria-pressed={playing}
          className={playing ? BUTTON_SECONDARY : BUTTON_PRIMARY}
          onClick={playing ? stop : play}
          type="button"
        >
          {playing ? strings.stop : strings.play}
        </button>
        <p
          aria-live="polite"
          className="flex items-center gap-2 text-foreground text-sm"
        >
          <span
            aria-hidden="true"
            className={`inline-block size-2.5 rounded-full ${
              playing
                ? "bg-primary motion-safe:animate-pulse"
                : "bg-muted-foreground/40"
            }`}
          />
          {status}
        </p>
        {remaining !== null && playing ? (
          <span className="text-muted-foreground text-sm tabular-nums">
            {t(strings.stopsIn, { time: formatClock(remaining) })}
          </span>
        ) : null}
      </div>
      {playing ? null : <p className={HINT}>{strings.startHint}</p>}

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <label className={LABEL} htmlFor="wn-volume">
            {strings.volumeLabel}:{" "}
            <span className="tabular-nums">
              {t(strings.percent, { value: volume })}
            </span>
          </label>
          <input
            className={RANGE}
            id="wn-volume"
            max={100}
            min={0}
            onChange={(event) => chooseVolume(Number(event.target.value))}
            type="range"
            value={volume}
          />
        </div>
        <div className="space-y-2">
          <label className={LABEL} htmlFor="wn-softness">
            {strings.softnessLabel}:{" "}
            <span className="tabular-nums">
              {t(strings.percent, { value: softness })}
            </span>
          </label>
          <input
            className={RANGE}
            id="wn-softness"
            max={100}
            min={0}
            onChange={(event) => chooseSoftness(Number(event.target.value))}
            type="range"
            value={softness}
          />
          <p className={HINT}>{strings.softnessHint}</p>
        </div>
      </div>

      <SelectField
        hint={strings.timerHint}
        id="wn-timer"
        label={strings.timerLabel}
        onChange={chooseMinutes}
        options={TIMER_CHOICES.map((value) => ({
          value: String(value),
          label:
            value === 0
              ? strings.timerNone
              : tPlural(strings.timerMinutes, locale, value),
        }))}
        value={minutes}
      />

      {error === "unsupported" ? (
        <ErrorNotice>{strings.unsupported}</ErrorNotice>
      ) : null}
      {error === "failed" ? <ErrorNotice>{strings.failed}</ErrorNotice> : null}
      <Notice>{strings.safety}</Notice>
      <p className={HINT}>{strings.privacy}</p>
    </div>
  );
}
